import { toast } from 'sonner';

import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { validateQuery } from '@/sections/insights/queryBuilder/utils/validateQuery';
import type { ValidationResult } from '@/utils/validation.util';

import { useSaveQuery } from './useSaveQuery';

/**
 * Hook for common query operations (execute, save, validate)
 *
 * Provides:
 * - handleExecute: Validates query and switches to execution mode
 * - handleSave: Validates query and saves to settings
 * - handleValidate: Returns validation result without side effects
 *
 * Eliminates code duplication between QueryEditor and QueryViewer
 */
export function useQueryOperations() {
  const {
    queryState,
    getSavedQuery,
    setMode,
    isGlobal,
    usernames,
    setQuery,
    setIsGlobal,
    setUsernames,
    mode,
    dispatch,
  } = useQueryBuilder();
  const { save, isSaving } = useSaveQuery();

  /**
   * Validates the current query and returns the result
   * @returns ValidationResult or null if no query state
   */
  const handleValidate = (): ValidationResult | null => {
    if (!queryState) {
      return null;
    }

    return validateQuery(queryState.flatGraph);
  };

  /**
   * Executes the current query after validation
   * - Validates query structure
   * - Shows error toast if invalid
   * - Adds selected usernames if in global mode
   * - Switches to query execution mode
   */
  const handleExecute = () => {
    // Validate before executing (queryState exists due to early return in components)
    if (!queryState) {
      toast.error('No query selected');
      return;
    }

    const validationResult = validateQuery(queryState.flatGraph);
    if (!validationResult.isValid) {
      toast.error(validationResult.errors[0]);
      return;
    }

    setMode('query');
  };

  /**
   * Saves the current query to user settings
   * - Validates query structure
   * - Shows error toast if invalid
   * - Updates query with custom-queries folderId
   * - Calls save mutation
   */
  const handleSave = async () => {
    try {
      // Validate before saving (queryState exists due to early return in components)
      if (!queryState) {
        toast.error('No query to save');
        return;
      }

      const validationResult = validateQuery(queryState.flatGraph);
      if (!validationResult.isValid) {
        toast.error(validationResult.errors[0]);
        return;
      }

      const savedQuery = getSavedQuery();
      if (!savedQuery) {
        toast.error('No query to save');
        return;
      }

      const updatedQuery = {
        ...savedQuery,
        folderId: 'custom-queries' as const,
      };

      await save(updatedQuery);
      dispatch({ type: 'SET_DIRTY', isDirty: false });
    } catch (error) {
      console.error('Failed to save query:', error);
      toast.error('Failed to save query');
    }
  };

  return {
    // Operations
    handleExecute,
    handleSave,
    handleValidate,
    // State for components
    isSaving,
    // Pass through context values for convenience
    queryState,
    setQuery,
    isGlobal,
    setIsGlobal,
    usernames,
    setUsernames,
    mode,
  };
}
